// Castintech dashboard — Budget Status view (Faz 30).
// Endpoint: GET /castintech-api/budget/status (Faz 25 PR-D-3).
// 3-tier badge: ok (<70%), warn (70-99%), err (>=100% — auto-stop tetikli).

import { apiFetch } from '../api.js';
import { escHtml, escAttr, $ } from '../utils.js';
import { renderError as dsRenderError, renderEmpty, createSkeleton } from '../ui/state-display.js';

// Backend response (notify/middleware/budget-check.js `buildBudgetStatusReport`):
//   { monthly_total_usd, tiers, agents: [{ agent_id, agent_name, spent_usd,
//     limit_usd, tier: 'ok'|'warning'|'critical'|'stop', pct (0..1 fraction) }],
//     generated_at }
// Frontend eski versiyon `spent`/`limit`/`status` alanlarını okuyordu (undefined
// kaynağı). Faz dashboard-m1: schema'ya hizalandı.
interface BudgetRow {
  agent_id: string;
  agent_name?: string;
  spent_usd?: number;
  limit_usd?: number;
  // legacy alias'ları da kabul et (test/mock için)
  spent?: number;
  limit?: number;
  pct: number; // 0..1 fraction
  currency?: string;
  tier?: 'ok' | 'warning' | 'critical' | 'stop' | string;
  status?: 'ok' | 'warn' | 'err' | string;
}

interface BudgetReport {
  rows?: BudgetRow[];
  agents?: BudgetRow[];
  generated_at?: string;
  period?: string;
  monthly_total_usd?: number;
}

let cache: BudgetReport = {};
let _loaded = false;

export async function loadBudgetStatus(): Promise<void> {
  // F4-A — İlk yüklemede skeleton.
  const root = $('budget-status-content');
  if (root && !_loaded) {
    root.innerHTML = '';
    const sk = createSkeleton({ rows: 4, height: '72px', rowGap: '10px' });
    root.appendChild(sk);
  }
  try {
    const r = await apiFetch('/castintech-api/budget/status');
    if (!r.ok) throw new Error('budget/status HTTP ' + r.status);
    cache = (await r.json()) as BudgetReport;
    _loaded = true;
    renderBudgetStatus();
  } catch (e) {
    console.warn('loadBudgetStatus failed:', (e as Error).message);
    _loaded = true;
    if (root) {
      // F4-A — state-display.ts renderError + Türkçe karakter düzeltmesi.
      dsRenderError(root, e as Error, {
        title: 'Bütçe durumu yüklenemedi',
        description: 'Bağlanırken sorun çıktı: ' + (e as Error).message,
        onRetry: () => { void loadBudgetStatus(); },
      });
    }
  }
}

function tierClass(pct: number): 'ok' | 'warn' | 'err' {
  if (pct >= 100) return 'err';
  if (pct >= 70) return 'warn';
  return 'ok';
}

// Backend tier ('ok'|'warning'|'critical'|'stop') → CSS class ('ok'|'warn'|'err').
function tierToCssClass(tier: string | undefined): 'ok' | 'warn' | 'err' | null {
  if (!tier) return null;
  const t = String(tier).toLowerCase();
  if (t === 'stop' || t === 'critical' || t === 'err') return 'err';
  if (t === 'warning' || t === 'warn') return 'warn';
  if (t === 'ok') return 'ok';
  return null;
}

function tierLabel(cls: string): string {
  if (cls === 'err') return 'AUTO-STOP';
  if (cls === 'warn') return 'UYARI';
  return 'OK';
}

export function renderBudgetStatus(): void {
  const root = $('budget-status-content');
  const sub = $('budget-status-sub');
  if (!root) return;
  const rows = cache.rows || cache.agents || [];
  if (sub) {
    sub.textContent = rows.length
      ? `${rows.length} ajan · dönem ${escHtml(cache.period || 'bu ay')}`
      : 'Bütçe kaydı yok — Meter henüz raporlamadı';
  }
  if (!rows.length) {
    // F4-A — state-display.ts renderEmpty + Türkçe ton.
    renderEmpty(root, {
      icon: '💰',
      title: 'Bütçe kaydı yok',
      description: 'Meter ajanı henüz bu dönem için bütçe raporu üretmedi. İlk Claude/OpenAI/OpenRouter çağrısından sonra burada görünür.',
    });
    return;
  }
  const cards = rows.map(r => {
    // Backend `pct` 0..1 fraction (örn 0.42 = %42). Legacy/mock %42.0 verirse de
    // çalış: 1.5'ten büyükse zaten yüzde olarak verilmiş kabul et.
    const rawPct = Number(r.pct) || 0;
    const pct = Math.max(0, Math.min(200, rawPct > 1.5 ? rawPct : rawPct * 100));
    const tier = tierToCssClass(r.tier) || tierToCssClass(r.status) || tierClass(pct);
    const cur = r.currency || 'USD';
    const barWidth = Math.min(100, pct).toFixed(1);
    const spent = r.spent_usd ?? r.spent;
    const limit = r.limit_usd ?? r.limit;
    const spentTxt = (typeof spent === 'number' && isFinite(spent)) ? spent.toFixed(2) : '—';
    const limitTxt = (typeof limit === 'number' && isFinite(limit)) ? limit.toFixed(2) : '—';
    return `
      <div class="hc-row" style="display:block;padding:10px 12px;border:1px solid var(--border, #2a3548);border-radius:6px;margin-bottom:8px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
          <span class="hc-dot ${tier}"></span>
          <b>${escHtml(r.agent_name || r.agent_id)}</b>
          <span class="hc-pill ${tier}" style="margin-left:auto;">${escHtml(tierLabel(tier))} · ${pct.toFixed(0)}%</span>
        </div>
        <div class="hc-bar"><div class="hc-bar-seg ${tier === 'err' ? 'fail' : (tier === 'warn' ? 'fallback' : 'primary')}" style="width:${barWidth}%"></div></div>
        <div class="hc-val mono" style="margin-top:4px;font-size:11px;color:var(--muted);">
          ${escHtml(spentTxt)} / ${escHtml(limitTxt)} ${escHtml(cur)}
        </div>
      </div>`;
  }).join('');
  root.innerHTML = cards;
}
